import type { Ref } from "vue";
import type {
  PublicCampaign,
  PublicEvent,
  PublicPlan,
  PublicOrganization,
} from "~/composables/useRechercheData";
import type { RechercheLiveResult } from "~/composables/useRechercheSearch";

export interface UseRechercheLiveSearchDeps {
  organizations: Ref<PublicOrganization[]>;
  events: Ref<PublicEvent[]>;
  adhesions: Ref<PublicPlan[]>;
  campaigns: Ref<PublicCampaign[]>;
}

/**
 * useRechercheLiveSearch — résultats live pour le panel header (debounced).
 * Extrait 1:1 de `pages/recherche/[[type]].vue` (Zone A split >800 lignes).
 *
 * Quand l'utilisateur tape dans le champ Quoi du header, on calcule les
 * 8 premiers matches (toutes catégories confondues : asso > event >
 * adhésion > cagnotte par ordre de pertinence). Debounce 150ms pour
 * éviter de re-render à chaque keystroke.
 */
export function useRechercheLiveSearch(deps: UseRechercheLiveSearchDeps) {
  const { organizations, events, adhesions, campaigns } = deps;
  const searchQuery = useRechercheSearchQuery();
  const sharedLiveResults = useRechercheLiveResults();

  // Helper image : les API renvoient des IDs Directus bruts (UUID), pas
  // des URLs absolues. On utilise getImageUrl du composable pour
  // transformer en URL servable (/api/assets/<id> via proxy Donoor).
  const { getImageUrl } = useRechercheHelpers();

  const computeLiveResults = (q: string): RechercheLiveResult[] => {
    const query = q.trim().toLowerCase();
    if (!query) return [];
    const results: RechercheLiveResult[] = [];

    // Associations
    for (const o of organizations.value) {
      if (results.length >= 8) break;
      if (o.name.toLowerCase().includes(query)) {
        results.push({
          type: "association",
          title: o.name,
          subtitle: o.full_address || undefined,
          href: `/associations/${o.slug}`,
          image: getImageUrl(o.logo, undefined, 200),
        });
      }
    }
    // Events
    for (const e of events.value) {
      if (results.length >= 8) break;
      if (e.title.toLowerCase().includes(query)) {
        results.push({
          type: "evenement",
          title: e.title,
          subtitle: e.organization_name,
          href: `/b/${e.slug}`,
          image: getImageUrl(e.cover_image, undefined, 200),
        });
      }
    }
    // Adhésions
    for (const p of adhesions.value) {
      if (results.length >= 8) break;
      if (p.title.toLowerCase().includes(query)) {
        results.push({
          type: "adhesion",
          title: p.title,
          subtitle: p.organization_name,
          href: `/a/${p.slug}`,
          image: getImageUrl(p.cover_image, undefined, 200),
        });
      }
    }
    // Cagnottes
    for (const c of campaigns.value) {
      if (results.length >= 8) break;
      if (c.title.toLowerCase().includes(query)) {
        results.push({
          type: "cagnotte",
          title: c.title,
          subtitle: c.organization_name,
          href: `/c/${c.slug}`,
          image: getImageUrl(c.image, undefined, 200),
        });
      }
    }
    return results;
  };

  // Debounce 150ms : on évite de recalculer (et surtout de pousser dans
  // useState global) à chaque keystroke. setTimeout natif suffit, pas
  // besoin d'une lib pour 1 cas.
  let liveTimer: ReturnType<typeof setTimeout> | null = null;
  watch(searchQuery, (q) => {
    if (liveTimer) clearTimeout(liveTimer);
    liveTimer = setTimeout(() => {
      sharedLiveResults.value = computeLiveResults(q);
    }, 150);
  });
}
